import 'chart.js/src/chart';

import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment/moment';
import { BaseChartDirective } from 'ng2-charts';
import { TranslateService } from 'ng2-translate';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';

import { ComparisonType } from '../../../../modules/domain/enums/comparisonType';
import { LineGraph, LineGraphData, LinegraphMaxY } from '../../../domain/models/linegraphData';
import { PerspectiveGraphValue } from '../../../domain/models/perspectiveGraphValue';
import { AverageGraphItem } from '../../../domain/models/averageGraphItem';
import { TimeRange } from '../../../domain/models/timeRange';
import ColorConstants from '../../../shared/constants/color-constants';
import { RouteDetailService } from '../../services/route-detail.service';
import { LinegraphSettings } from '../../viewModels/lineGraph-settings-viewmodel';
import { RouteIdViewModel } from '../../viewModels/route-id-viewmodel';
import { SelectedDay } from '../../viewModels/selectedDay-viewmodel';

declare var Chart;
@Component({
    selector: 'app-linegraph',
    templateUrl: './linegraph.component.html',
    styleUrls: ['./linegraph.component.scss']
})
export class LinegraphComponent implements OnInit, OnDestroy {
    public yAxisDefinitions: any = {
        freeTravelTime: {
            max: 1000,
            min: 0,
            stepSize: 5
        },
        realTravelTime: {
            max: 160,
            min: 0,
            stepSize: 5
        },
        realTravelSpeed: {
            max: 140,
            min: 0,
            stepSize: 20
        },
        freetravelSpeed: {
            max: 140,
            min: 0,
            stepSize: 20
        },
        effectiveSpeed: {
            max: 140,
            min: 0,
            stepSize: 20
        },
        delay: {
            max: 160,
            min: 0,
            stepSize: 10
        }
    };

    public lineChartData: Array<any> = [];
    public lineChartLabels: Array<any> = [];
    public lineChartLegend: Array<any> = [];
    public lineChartOptions: any = {
        scales: {
            xAxes: [{
                gridLines: {
                    color: 'rgba(171,171,171,1)',
                    lineWidth: 1
                },
                scaleLabel: {
                    display: true,
                    labelString: 'TijdStip'
                }
            }],
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    max: 160,
                    min: 0,
                    stepSize: 40
                },
                scaleLabel: {
                    display: true,
                    labelString: 'km/u'
                }
            }],
            responsive: true,
        },
        lineTension: 0,
        cubicInterpolationMode: 'default',
        skipLabels: 3,
        legend: {
            onClick: (e: MouseEvent, legendItem: any) => this.legendClicked(e, legendItem)
        },
        plugins: {
            datalabels: {
              formatter: (value, ctx) => {
                let percentageString = '';
                return percentageString;
              },
            }
          },
    };

    public lineChartColors: Array<any> = ColorConstants.GraphColors;

    public lineChartType = 'line';

    public rangeValues = [0, 0];
    public selectedRange = [0, 0];
    public maxIndexLineGraph: number;
    public labels: Array<string>;

    public availableDates: SelectedDay[];

    @Input() linegraphSettingsChanged: Observable<LinegraphSettings>;
    @Input() downloadObs: Observable<any>;
    @Input() routeName: string;
    @Output() onCanvasReady: EventEmitter<ElementRef> = new EventEmitter();

    @ViewChild('linegraph') linegraph: ElementRef;
    @ViewChild(BaseChartDirective) private _chart;

    private subscriptions: Subscription[];

    private settings: LinegraphSettings;
    private linegraphData: LineGraphData[];
    private outputLinegraphData: LineGraphData[];
    private perspectiveData: PerspectiveGraphValue;
    private comparisonData: LineGraph[];
    private comparisonType: ComparisonType;
    private averageGraph: AverageGraphItem[];
    private maximums: LinegraphMaxY;
    private averageLabel: string;
    private averageHidden: boolean;

    private fullData: Array<any>;
    private fullLabels: Array<any>;
    private fullPerspective: Array<any>;
    private fullMultiDay: Array<any>;

    private rangeChange$: Subject<any>;
    sliderStep: number;

    private dataIsReady: boolean;
    private noData: boolean;

    constructor(private translate: TranslateService,
        private routeDetailService: RouteDetailService) { };

    ngOnInit() {
        this.subscriptions = [];
        this.comparisonData = [];
        this.comparisonType = ComparisonType.None;
        this.outputLinegraphData = [];
        this.averageGraph = [];
        this.averageHidden = true;
        this.noData = false;
        this.dataIsReady = false;
        this.rangeChange$ = new Subject<number[]>();
        this.sliderStep = 1;
        this.averageLabel = this.translate.instant('AVERAGE');

        if (this.downloadObs) {
            this.subscriptions.push(this.downloadObs.subscribe(() => {
                this.onCanvasReady.emit(this.linegraph);
            }));
        }
        this.subscribeToSettings();
        this.subscribeRangeChanges();

        this.translate.onLangChange.subscribe(() => {
            this.updateLinegraph(this.settings);
        });
    }

    private subscribeToSettings(): void {
        this.subscriptions.push(
            this.linegraphSettingsChanged.subscribe((newSettings: LinegraphSettings) => {
                if (newSettings) {
                    this.comparisonData = [];
                    if (newSettings.selectedDates.length > 0) {
                        this.comparisonType = ComparisonType.Days;
                        this.getMultiDayData(newSettings);
                    } else if (newSettings.selectedRouteIds.length > 0) {
                        this.comparisonType = ComparisonType.Routes;
                        this.getMultiRouteData(newSettings);
                    } else if (newSettings.compareRandomRoute !== undefined && newSettings.compareRandomDates !== undefined) {
                        this.comparisonType = ComparisonType.Random;
                        this.getRandomCompareData(newSettings);
                    } else {
                        this.comparisonType = ComparisonType.None;
                        this.updateLinegraph(newSettings);
                    }
                }
            })
        );
    }

    private legendClicked(e: MouseEvent, legendItem: any): any {
        let ci = this._chart.chart;
        let meta = ci.getDatasetMeta(legendItem.datasetIndex);
        if (legendItem.text === this.averageLabel) {
            this.averageHidden = !this.averageHidden;
            meta.hidden = this.averageHidden;
        } else {
            meta.hidden = meta.hidden ? false : true;
        }
        ci.update();
    }

    private getMultiDayData(newSettings: LinegraphSettings): void {
        this.routeDetailService.getMultiDaylineGraph(newSettings.routeId, newSettings.selectedDates)
            .subscribe((result: LineGraph[]) => {
                this.comparisonData = result;
                this.updateLinegraph(newSettings);
            });
    }

    private getMultiRouteData(newSettings: LinegraphSettings) {
        this.joinMultiRouteData(newSettings).subscribe((result: LineGraph[]) => {
            this.sortComparisonDataByRouteIds(result, newSettings.selectedRouteIds);
            this.updateLinegraph(newSettings);
        });
    }

    private getRandomCompareData(newSettings: LinegraphSettings): void {
        this.joinMultiDayData(newSettings.compareRandomDates, newSettings.compareRandomRoute.id)
            .subscribe((result: LineGraph[]) => {
                this.comparisonData = [];

                _.each(result, (data: LineGraph) => {
                    let comparisonitem = data;
                    comparisonitem.name = new Date(data.date).toLocaleDateString();
                    this.comparisonData.push(comparisonitem);
                });

                this.updateLinegraph(newSettings);
            });
    }

    private joinMultiDayData(dates: Date[], routeId: string): Observable<{}> {
        let subscriptions: Observable<{} | LineGraph>[] = [];
        _.each(dates, (date: Date) => {
            subscriptions.push(this.routeDetailService.getDataForSettings(date, routeId));
        });
        return Observable.forkJoin(subscriptions);
    }

    private joinMultiRouteData(newSettings: LinegraphSettings): Observable<{}> {
        let subscriptions: Observable<{} | LineGraph>[] = [];
        _.each(newSettings.selectedRouteIds, (route: RouteIdViewModel) => {
            subscriptions.push(
                this.routeDetailService.getDataForSettings(newSettings.date, route.id));
        });
        return Observable.forkJoin(subscriptions);
    }

    private sortComparisonDataByRouteIds(linegraphData: LineGraph[], selectedRouteIds: RouteIdViewModel[]): void {
        let result: LineGraph[] = [];
        _.each(selectedRouteIds, (routeId: RouteIdViewModel) => {
            let comparisonItem = _.find(linegraphData, (cd: LineGraph) => {
                return cd.jobId === routeId.id;
            });
            if (comparisonItem) {
                comparisonItem.name = routeId.name;
                result.push(comparisonItem);
            }
        });

        this.comparisonData = result;
    }

    private updateLinegraph(newSettings: LinegraphSettings): void {
        if (!this.settings || this.settings.routeId !== newSettings.routeId || !this.settings.date || !newSettings.date || this.settings.date.getTime() !== newSettings.date.getTime()) {
            this.fullMultiDay = undefined;
            this.availableDates = undefined;
            this.settings = newSettings;
            this.getLinegraphData(newSettings.interval);
        } else {
            this.settings = newSettings;
            this.drawLineGraph(newSettings.interval);
        }
    }

    private getLinegraphData(interval: number): void {
        this.routeDetailService.getDataForSettings(this.settings.date, this.settings.routeId)
            .subscribe((result: LineGraph) => {
                if (result.graphValues) {
                    this.linegraphData = result.graphValues;
                    this.routeDetailService.getPerspective(this.settings.routeId, this.settings.date.getDay())
                        .subscribe((perspectiveResult: PerspectiveGraphValue) => {
                            this.noData = false;
                            this.perspectiveData = perspectiveResult;
                            this.drawLineGraph(interval);
                        }, (error: any) => {
                            this.noData = false;
                            this.perspectiveData = undefined;
                            this.drawLineGraph(interval);
                        });
                } else {
                    this.noData = true;
                    this.linegraphData = undefined;
                }
            }, (error: any) => {
                this.noData = true;
                this.linegraphData = undefined;
            });
    }

    private drawLineGraph(interval: number): void {
        let translatedMode = this.translate.instant(this.settings.modeLabel);

        if (this.linegraphData) {
            let xAxisLabels = this.getXAxisLabels(interval);
            this.outputLinegraphData = this.rescaleLinegraphData(this.linegraphData, xAxisLabels);

            let graphPoints: number[] = [];
            this.averageGraph = [];
            _.each(this.outputLinegraphData, (lg: LineGraphData) => {
                const dataPoint = lg[this.settings.mode];
                graphPoints.push(dataPoint);
                let div = dataPoint === null || dataPoint === undefined ? 0 : 1;
                this.averageGraph.push(new AverageGraphItem({ value: dataPoint, divider: div }));
            });

            this.maximums = this.findMaximums();
            this.dataIsReady = false;
            this.clearData();

            this.maxIndexLineGraph = xAxisLabels.length - 1;
            if (this.selectedRange[1] === 0 || this.selectedRange[1] > this.maxIndexLineGraph) {
                this.selectedRange[1] = this.maxIndexLineGraph;
                this.rangeValues = this.selectedRange;

                if (this.maxIndexLineGraph >= 200 && this.maxIndexLineGraph < 500) {
                    this.sliderStep = 2;
                } else if (this.maxIndexLineGraph >= 500 && this.maxIndexLineGraph < 1000) {
                    this.sliderStep = 5;
                } else if (this.maxIndexLineGraph >= 1000) {
                    this.sliderStep = 10;
                }
            }
            setTimeout(() => {
                this.labels = xAxisLabels;
                this.fullData.push({ data: graphPoints, label: translatedMode });
                this.fullLabels = xAxisLabels;

                if (this.selectedRange && this.selectedRange[0] && this.selectedRange[1]) {
                    this.lineChartLabels = this.fullLabels.slice(this.selectedRange[0], this.selectedRange[1] + 1);
                    this.lineChartData.push({ data: this.fullData[0].data.slice(this.selectedRange[0], this.selectedRange[1] + 1), label: translatedMode });
                } else {
                    this.lineChartLabels = xAxisLabels;
                    this.lineChartData.push({ data: graphPoints, label: translatedMode });
                }

                if (this.comparisonType === ComparisonType.None && this.perspectiveData) {
                    this.setLineGraphNoComparison();
                } else if (this.comparisonType !== ComparisonType.None) {
                    this.setLineGraphComparison(xAxisLabels);
                }

                this.setAxisLabels();
                this.setLineChartOptions(graphPoints);

                this.dataIsReady = true;
                this.forceChartRefresh();
            }, 0);
        }
    }

    private setLineGraphNoComparison(): void {
        const perspectivePoints: number[] = [];
        let perspectiveItem = this.perspectiveData[this.settings.perspective];

        perspectiveItem.forEach((perspectiveGraphData: any, i: number) => {
            let averageGraphItem = this.averageGraph[i];
            if (!averageGraphItem) { return; }

            const perspectivePoint = perspectiveGraphData[this.settings.mode];
            perspectivePoints.push(perspectivePoint);

            averageGraphItem.divider = 1;
            if (perspectivePoint !== null && perspectivePoint !== undefined && averageGraphItem.value !== null && averageGraphItem.value !== undefined) {
                averageGraphItem.divider = 2;
            }

            averageGraphItem.value = (averageGraphItem.value + perspectivePoint) / averageGraphItem.divider;
        });

        const translatedPerspective = this.translate.instant(this.settings.perspectiveLabel);

        this.fullPerspective.push({ data: perspectivePoints, label: translatedPerspective });

        if (this.selectedRange && this.selectedRange[0] && this.selectedRange[1]) {
            this.lineChartData.push({ data: perspectivePoints.slice(this.selectedRange[0], this.selectedRange[1] + 1), label: translatedPerspective });
            this.lineChartData.push({ data: this.averageGraph.map((g: AverageGraphItem) => g.value).slice(this.selectedRange[0], this.selectedRange[1] + 1), label: this.averageLabel, hidden: this.averageHidden });
        } else {
            this.lineChartData.push({ data: perspectivePoints, label: translatedPerspective });
            this.lineChartData.push({ data: this.averageGraph.map((g: AverageGraphItem) => g.value), label: this.averageLabel, hidden: this.averageHidden });
        }
    }

    private setLineGraphComparison(xAxisLabels: string[]): void {
        this.rescaleComparisonData(xAxisLabels);
        this.changePrimaryRouteLabel();

        this.comparisonData.forEach((lineGraph: LineGraph, i: number) => {
            if (lineGraph && lineGraph.graphValues) {
                let chartData = [];
                let chartLegend: string;
                let chartColor: any = null;

                if (i < this.lineChartColors.length) {
                    chartColor = this.lineChartColors[i + 1];
                }

                const dateLabel: string = this.getTranslatedLabel(new Date(lineGraph.date));

                switch (this.comparisonType) {
                    case ComparisonType.Days:
                        chartLegend = dateLabel;
                        break;
                    case ComparisonType.Routes:
                        this.lineChartLegend.push({ label: lineGraph.name, chartColor: chartColor });
                        chartLegend = this.truncateLabel(lineGraph.name, 15);
                        break;
                    case ComparisonType.Random:
                        chartLegend = this.truncateLabel(dateLabel, 15);
                        break;
                }

                lineGraph.graphValues.forEach((graphValue: LineGraphData, j: number) => {
                    let averageGrapItem = this.averageGraph[j];
                    if (!averageGrapItem) { return; }

                    const graphPoint = graphValue[this.settings.mode];
                    chartData.push(graphPoint);
                    averageGrapItem.value += graphPoint;
                    if (graphPoint === null || graphPoint === undefined) { return; }
                    averageGrapItem.divider += 1;
                });

                this.fullMultiDay.push({ data: chartData, label: chartLegend });

                if (this.selectedRange && this.selectedRange[0] && this.selectedRange[1]) {
                    this.lineChartData.push({ data: chartData.slice(this.selectedRange[0], this.selectedRange[1] + 1), label: chartLegend });
                } else {
                    this.lineChartData.push({ data: chartData, label: chartLegend });
                }
            }
        });

        for (let i = 0; i < this.averageGraph.length; i++) {
            this.averageGraph[i].value = this.averageGraph[i].value / (this.averageGraph[i].divider);
        };

        if (this.selectedRange && this.selectedRange[0] && this.selectedRange[1]) {
            this.lineChartData.push({ data: this.averageGraph.map((g: AverageGraphItem) => g.value).slice(this.selectedRange[0], this.selectedRange[1] + 1), label: this.averageLabel, hidden: this.averageHidden });
        } else {
            this.lineChartData.push({ data: this.averageGraph.map((g: AverageGraphItem) => g.value), label: this.averageLabel, hidden: this.averageHidden });
        }

        if (this.comparisonType === ComparisonType.Routes) {
            this.lineChartLegend.push({ label: this.averageLabel, chartColor: this.lineChartColors[this.comparisonData.length + 1] });
        }
    }

    getTranslatedLabel(date: Date): string {
        const dayName: string = moment(date).format('dddd').toUpperCase();
        const dayTranslated: string = this.translate.instant(dayName);
        const dayLabel = `${dayTranslated} ${moment(date).format('DD/MM/YYYY')}`;
        return dayLabel;
    }

    updateTranslatedLabel(): void {
        this.lineChartData.forEach((dataset: any, index: number) => {
            const date: string = dataset.label.split(' ')[1];
            if (date && moment(date).isValid()) {
                const momentDate: Date = moment(date, 'DD/MM/YYYY').toDate();
                const dateLabel: string = this.getTranslatedLabel(momentDate);
                this.lineChartData[index].label = this.truncateLabel(dateLabel, 15);
            }
        });
        this.forceChartRefresh();
    }

    private changePrimaryRouteLabel(): void {
        let fulllabel = this.getTranslatedLabel(this.settings.date);
        let truncatedLabel = fulllabel;
        if (this.comparisonType === ComparisonType.Routes) {
            fulllabel = this.routeName;
            truncatedLabel = this.truncateLabel(this.routeName, 15);
        }

        this.lineChartData[0].label = truncatedLabel;
        if (this.lineChartLegend.length === 0 || (this.lineChartLegend.length > 0 && this.lineChartLegend[0].label !== fulllabel)) {
            this.lineChartLegend.unshift({ label: fulllabel, chartColor: ColorConstants.GraphColors[0] });
        }
    }

    private truncateLabel(label: string, length: number): string {
        if (label.length > length) {
            return label.substring(0, length) + '...';
        }
        return label;
    }

    private legendHasColor(legend: any): boolean {
        if (legend.chartColor) {
            return true;
        }
        return false;
    }

    private showChartLegend(): boolean {
        return !this.noData && this.comparisonType === ComparisonType.Routes && this.lineChartLegend && this.lineChartLegend.length > 0;
    }

    private rescaleComparisonData(xAxisLabels: string[]): void {
        _.each(this.comparisonData, (g: LineGraph) => {
            g.graphValues = this.rescaleLinegraphData(g.graphValues, xAxisLabels);
        });
    }

    private rescaleLinegraphData(linegraphData: LineGraphData[], xAxisLabels: string[]): LineGraphData[] {
        let rescaledLinegraphData: LineGraphData[] = [];

        _.each(xAxisLabels, (label: string) => {
            let dataItem: LineGraphData = _.find(linegraphData, (lg: LineGraphData) => {
                return lg.time.substring(0, 5) === label;
            });

            if (dataItem) {
                rescaledLinegraphData.push(dataItem);
            } else {
                rescaledLinegraphData.push(new LineGraphData({
                    time: label,
                    freeTravelTime: null,
                    realTravelTime: null,
                    freetravelSpeed: null,
                    realTravelSpeed: null,
                    effectiveSpeed: null,
                    delay: null
                }));
            }
        });

        return rescaledLinegraphData;
    }

    private getXAxisLabels(interval: number): string[] {
        let timeRange: TimeRange = this.getTimeRange();

        let result: string[] = [];

        if (interval > 0 && timeRange.min && timeRange.max) {
            let iterator: moment.Moment = timeRange.min;
            while (!iterator.isAfter(timeRange.max)) {
                result.push(iterator.format('HH:mm'));
                iterator.add(interval, 'minutes');
            }
        } else if (timeRange.min) {
            result.push(timeRange.min.format('HH:mm'));
        }

        return result;
    }

    private getTimeRange(): TimeRange {
        let timeRange: TimeRange = new TimeRange();

        _.each(this.comparisonData, (data: LineGraph) => {
            timeRange = this.getTimeRangeFromData(data.graphValues, timeRange);
        });

        return this.getTimeRangeFromData(this.linegraphData, timeRange);
    }

    private getTimeRangeFromData(linegraphData: LineGraphData[], input: TimeRange): TimeRange {
        let timeRange: TimeRange = input;

        if (linegraphData && linegraphData.length > 0) {
            let minTime = this.getMinTime(linegraphData);
            if (!timeRange.min || minTime.isBefore(timeRange.min)) {
                timeRange.min = minTime;
            }

            let maxTime = this.getMaxTime(linegraphData);
            if (!timeRange.max || maxTime.isAfter(timeRange.max)) {
                timeRange.max = maxTime;
            }
        }

        return timeRange;
    }

    private getMinTime(linegraphData: LineGraphData[]): moment.Moment {
        const result = _.minBy(linegraphData, (data: LineGraphData) => {
            return data.time;
        });
        return moment(result.time, 'HH:mm:ss');
    }

    private getMaxTime(linegraphData: LineGraphData[]): moment.Moment {
        const result = _.maxBy(linegraphData, (data: LineGraphData) => {
            return data.time;
        });
        return moment(result.time, 'HH:mm:ss');
    }

    private clearData(): void {
        this.lineChartData = [];
        this.lineChartLegend = [];
        this.fullPerspective = [];
        this.fullLabels = [];
        this.fullData = [];
        this.fullMultiDay = [];
    }

    private setLineChartOptions(data: any[]): void {
        let newY = this.yAxisDefinitions[this.settings.mode];
        let step = Math.floor(((this.maximums[this.settings.mode] / 15) / 5) * 5);

        this.lineChartOptions.scales.yAxes[0].ticks.min = newY.min;
        this.lineChartOptions.scales.yAxes[0].ticks.max = this.maximums[this.settings.mode];
        this.lineChartOptions.scales.yAxes[0].ticks.stepSize = step;

        if (this.settings.yValue != null && this.settings.yValue !== undefined) {
            this.lineChartOptions.scales.yAxes[0].ticks.max = this.settings.yValue;
            step = Math.floor(((this.settings.yValue / 15) / 5) * 5);
            this.lineChartOptions.scales.yAxes[0].ticks.stepSize = step;
        }
    }

    private setAxisLabels(): void {
        this.lineChartOptions.scales.xAxes[0].scaleLabel.labelString = this.translate.instant('TIME_LABEL');

        switch (this.settings.mode) {
            case 'freeTravelTime':
            case 'realTravelTime':
                this.lineChartOptions.scales.yAxes[0].scaleLabel.labelString = this.translate.instant('MINUTES');
                break;
            case 'delay':
                this.lineChartOptions.scales.yAxes[0].scaleLabel.labelString = this.translate.instant('SEC_KM');
                break;
            default:
                this.lineChartOptions.scales.yAxes[0].scaleLabel.labelString = this.translate.instant('KM_H');
                break;
        }
    }

    private forceChartRefresh(): void {
        setTimeout(() => {
            if (this._chart) {
                this._chart.refresh();
            }
        }, 10);
    }

    private findMaximums(): LinegraphMaxY {
        let max: LinegraphMaxY = new LinegraphMaxY();
        _.each(this.comparisonData, (data: LineGraph) => {
            max = this.findMaxYValueFromData(data.graphValues, max);
        });
        return this.findMaxYValueFromData(this.outputLinegraphData, max);
    }

    private findMaxYValueFromData(linegraphData: LineGraphData[], input: LinegraphMaxY): LinegraphMaxY {
        let max: LinegraphMaxY = input;
        _.each(linegraphData, (datapoint: LinegraphMaxY) => {
            if (max.freeTravelTime < datapoint.freeTravelTime * 1.30) {
                max.freeTravelTime = Math.round(datapoint.freeTravelTime * 1.30 / 10) * 10;
            }
            if (max.realTravelTime < datapoint.realTravelTime * 1.30) {
                max.realTravelTime = Math.round(datapoint.realTravelTime * 1.30 / 10) * 10;
            }
            if (max.freetravelSpeed < datapoint.freetravelSpeed * 1.30) {
                max.freetravelSpeed = Math.round(datapoint.freetravelSpeed * 1.30 / 10) * 10;
            }
            if (max.realTravelSpeed < datapoint.realTravelSpeed * 1.30) {
                max.realTravelSpeed = Math.round(datapoint.realTravelSpeed * 1.30 / 10) * 10;
            }
            if (max.effectiveSpeed < datapoint.effectiveSpeed * 1.30) {
                max.effectiveSpeed = Math.round(datapoint.effectiveSpeed * 1.30 / 10) * 10;
            }
            if (max.delay < datapoint.delay * 1.30) {
                max.delay = Math.round(datapoint.delay * 1.30);
            }
        });

        return max;
    }

    rangeChanged(range): void {
        this.rangeChange$.next(this.rangeValues);
    }

    private subscribeRangeChanges(): void {
        this.subscriptions.push(
            this.rangeChange$
                .debounceTime(150)
                .subscribe((range: number[]) => {
                    this.lineChartLabels = [];
                    this.lineChartData = [];
                    this.selectedRange = range;

                    this.lineChartLabels = this.fullLabels.slice(this.selectedRange[0], this.selectedRange[1] + 1);
                    this.lineChartData.push({
                        data: this.fullData[0].data.slice(this.selectedRange[0], this.selectedRange[1] + 1),
                        label: this.translate.instant(this.settings.modeLabel)
                    });

                    if (this.fullPerspective[0]) {
                        this.lineChartData.push({
                            data: this.fullPerspective[0].data.slice(this.selectedRange[0], this.selectedRange[1] + 1),
                            label: this.translate.instant(this.settings.perspectiveLabel)
                        });
                    }

                    if (this.fullMultiDay && this.fullMultiDay.length > 0) {
                        _.each(this.fullMultiDay, (day: any) => {
                            this.lineChartData.push({
                                data: day.data.slice(this.selectedRange[0], this.selectedRange[1] + 1),
                                label: day.label
                            });
                        });
                    }

                    if (this.averageGraph && this.averageGraph.length > 0) {
                        this.lineChartData.push({
                            data: this.averageGraph.map((g: AverageGraphItem) => g.value).slice(this.selectedRange[0], this.selectedRange[1] + 1),
                            label: this.averageLabel,
                            hidden: this.averageHidden
                        });
                    }

                    if (this.comparisonType !== ComparisonType.None) {
                        this.changePrimaryRouteLabel();
                    }

                    this.forceChartRefresh();
                }));
    }

    getLabelByIndex(index: number): string {
        if (this.labels && this.labels.length > index) {
            return this.labels[index];
        } else {
            return '...';
        }
    }

    dataReady(): boolean {
        return this.dataIsReady;
    }

    noDataAvailable(): boolean {
        return this.noData;
    }

    ngOnDestroy() {
        _.each(this.subscriptions, (sub: Subscription) => {
            sub.unsubscribe();
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/route-detail/components/linegraph/linegraph.component.ts